import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex w-full mb-3" }
const _hoisted_2 = { class: "ml-2 flex flex-column" }
const _hoisted_3 = { class: "align-items-center inline-block text-sm" }
const _hoisted_4 = {
  key: 0,
  class: "dark-light inline-block"
}
const _hoisted_5 = ["textContent"]
const _hoisted_6 = ["textContent"]
const _hoisted_7 = {
  key: 1,
  class: "dark-light inline-block"
}
const _hoisted_8 = ["textContent"]
const _hoisted_9 = {
  key: 2,
  class: "dark-light inline-block"
}
const _hoisted_10 = {
  key: 3,
  class: "dark-light inline-block"
}
const _hoisted_11 = ["textContent"]
const _hoisted_12 = ["textContent"]
const _hoisted_13 = ["textContent"]
const _hoisted_14 = ["textContent"]
const _hoisted_15 = {
  key: 7,
  class: "inline"
}
const _hoisted_16 = ["textContent"]
const _hoisted_17 = ["textContent"]
const _hoisted_18 = ["textContent"]
const _hoisted_19 = ["textContent"]
const _hoisted_20 = ["textContent"]
const _hoisted_21 = ["textContent"]
const _hoisted_22 = ["textContent"]
const _hoisted_23 = ["textContent"]
const _hoisted_24 = ["innerHTML"]
const _hoisted_25 = ["textContent"]
const _hoisted_26 = ["textContent"]

import { computed, toRefs } from 'vue';
import { RRule } from 'rrule';
import download from 'downloadjs';
import { useRouter } from 'vue-router';
import { RSMService } from '@/services';
import { can, useDateTime } from '@/composables';
import { ACTIVITY_LOG_IMPORT, PERMISSIONS, SUBJECT_TYPE, STATUS_IMPORT_RSM, ACTIVITY_EVENT_TYPE } from '@/enum';
import { useDocumentsStore, useCalendarStore } from '@/store';

interface LogItemPropsInterface {
  accessTenant?: {
    full_name_user: string;
    id: number;
    tenant_id: number;
    tenant_name: string;
  };
  impersonatedBy?: {
    id: number;
    full_name: string;
  };
  causer: {
    id: number;
    full_name: string;
    therapy_event_type: string;
  };
  subject: {
    id: number;
    full_name?: string;
    name?: string;
    therapy_event_type?: string;
    start_at?: string;
    treatment_goal_type_id?: string;
    student?: {
      id: number;
      full_name: string;
    };
    event_display?: string;
    acronym?: string;
    processed_at?: string;
    status?: string;
    error_file?: string;
    file?: string;
  };
  subjectType: string;
  timeAgo: string;
  changes?: string;
  event: string;
  custom: {
    description: string;
    icon: string;
  };
  description?: string;
  properties: {
    changes: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new_values: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    old_values: any;
    request?: { event_date: string; notes: string; therapy_result_type_id: number; therapy_event_ids: number[] };
    path: string;
    tab: string;
  };
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ActivityLogItem',
  props: {
    accessTenant: {},
    impersonatedBy: {},
    causer: {},
    subject: {},
    subjectType: {},
    timeAgo: {},
    changes: {},
    event: {},
    custom: {},
    description: {},
    properties: {}
  },
  setup(__props: any) {

const props = __props;
const {
  impersonatedBy,
  event,
  causer,
  subject,
  subjectType,
  custom,
  timeAgo,
  changes,
  properties,
  accessTenant,
  description,
} = toRefs(props);
const documentStore = useDocumentsStore();
const subjectIsEvent = () => subjectType.value === SUBJECT_TYPE.THERAPY_EVENT;
const subjectIsTherapyResult = () => event.value === ACTIVITY_EVENT_TYPE.THERAPY_RESULT;
const subjectIsBulkAttendance = () => event.value === ACTIVITY_EVENT_TYPE.BULK_ATTENDANCE;
const subjectIsImpersonate = () => event.value === ACTIVITY_EVENT_TYPE.IMPERSONATE;
const subjectIsTreatmentGoal = () => subjectType.value === SUBJECT_TYPE.TREATMENT_GOAL;
const subjectIsOrganization = () => subjectType.value === SUBJECT_TYPE.ORGANIZATION;
const subjectIsRsmImport = () => subjectType.value === SUBJECT_TYPE.FILE_IMPORT;
const isImport = computed<boolean>(() => event.value === ACTIVITY_LOG_IMPORT.EVENT);
const route = useRouter();
const calendarStore = useCalendarStore();

/**
 * set the bulk import text with no hyperlink
 */
const bulkImportText = () => {
  if (subjectIsBulkAttendance())
    return properties.value.request ? properties.value.request.event_date : subject.value.event_display;
};

/**
 * set the main text based on start/end date and subject values
 */
const subjectText = () => {
  if (event.value === SUBJECT_TYPE.LOGIN || subjectIsBulkAttendance()) return '';

  if (subjectIsEvent() || subjectIsTherapyResult()) {
    const startTime = new Intl.DateTimeFormat('default', {
      hour: 'numeric',
      minute: 'numeric',
    }).format(useDateTime().fromUTCStringToLocalDate(subject.value.start_at as string));

    const eventDate = properties.value?.request?.event_date || subject.value.event_display;
    return `${eventDate} at ${startTime.toUpperCase().replace(' ', '')}`;
  }

  if (subjectIsTreatmentGoal()) return subject.value.student?.full_name;
  if (subjectIsOrganization()) return subject.value.name;
  if (subjectIsImpersonate()) return subject.value.full_name;
  if (subjectIsRsmImport()) {
    const processed_at = subject.value.processed_at;
    const subjectName = subject.value.file;
    const nameParts = subjectName.split('/');
    // fallbackName assumes that the array has a length of 4
    const fallbackName = nameParts[nameParts.length - 1] || 'unknown';

    return `rsm-import-${processed_at ? processed_at.split(' ')[0] + '.csv' : fallbackName}`;
  }
};

const evaluateStatusToImport = () => {
  switch (subject.value.status) {
    case STATUS_IMPORT_RSM.ERRORS:
      return ' with some errors - ';
    case STATUS_IMPORT_RSM.COMPLETED:
      return ' with no errors';
    case STATUS_IMPORT_RSM.PROCESSING:
      return ' processing';
    default:
      return ' pending to import';
  }
};

/**
 * find the start at , end at, or duration in string changes
 * @param str
 */
const parseStartEnd = (str: string, searchString: string) => {
  const split = str.split(',');
  let returnStr = '';
  split.forEach((txt) => {
    if (txt.match(searchString)) {
      returnStr = txt.trim();
      const returnFromDate = txt
        .substring(txt.indexOf(searchString) + (searchString.length + 1), searchString.length + 21)
        .trim();

      const returnToDate = txt.substring(returnStr.indexOf('to') + 3, returnStr.indexOf('to') + 23).trim();
      returnStr = `${searchString} ${splitStringIntoReadableDate(returnFromDate)} to ${splitStringIntoReadableDate(
        returnToDate,
      )} `;
    }
  });

  return returnStr;
};

const parseDuration = (str: string) => str.substring(str.indexOf('duration from', 25)).trim();

/**
 * split the unreadable UNTIL=yyyymmdd z string into a formatted date
 */
const splitStringIntoReadableDate = (dateStr: string) => {
  const dateArr = dateStr.split('-');

  const yyyy = dateArr[0];
  const mm = dateArr[1];
  //now split day time
  const dayTime = dateArr[2].split(' ');
  const dd = dayTime[0];
  //get the parsed time and format it
  const time = dayTime[1] ? useDateTime().formatAMPMTime(dateStr) : '';

  return ` ${mm}/${dd}/${yyyy} ${time}`;
};

/**
 * format the RRULE 'changes' string into something more readable
 * for example:
 * original string: 'from DTSTART:20230105T230000Z RRULE:FREQ=WEEKLY;UNTIL=20230106T010000Z to '
 * converts to:  'from 01/05/2023 to 01/05/2023 frequency=WEEKLY, until 01/06/2023'
 */
const formatChanges = (txt: string): string => {
  let startAt, endAt, freq, rruleObj;
  if (txt.includes('RRULE')) {
    const split = txt.split(';');
    split.forEach((rule) => {
      if (rule.toUpperCase().match('UNTIL=')) {
        rruleObj = RRule.parseString(rule.substring(rule.indexOf('UNTIL='), rule.indexOf('Z') + 1).trim());
        endAt = ` end at ${useDateTime().formatLocalDateTime(rruleObj.until, true, true)}`;
      }

      if (rule.toUpperCase().match('DTSTART')) {
        rruleObj = RRule.parseString(rule.substring(rule.indexOf('DTSTART')).trim());
        startAt = ` start at ${useDateTime().formatLocalDateTime(rruleObj.dtstart, true, true)}`;
      }

      if (rule.toUpperCase().match('FREQ')) {
        rruleObj = rule.substring(rule.indexOf('FREQ=') + 5).trim();
        freq = ` frequency ${rruleObj}`;
      }
    });
  } else {
    //else it is NOT an RRULE event just a single day which is formatted diff and freq is duration
    startAt = txt.includes('start at from') ? parseStartEnd(txt, 'start at from') : '';
    endAt = txt.includes('end at from') ? parseStartEnd(txt, 'end at from') : '';
    freq = txt.includes('duration from') ? parseDuration(txt) : '';
  }
  return `${startAt}, ${endAt} ${freq}`;
};

const routeToRedirect = async () => {
  if (subjectIsImpersonate()) return route.replace(`/user/${subject.value.id}`);

  if (subjectIsEvent() || subjectIsTherapyResult()) {
    calendarStore.redirectedToDate = useDateTime().fromUTCStringToLocalDate(subject.value.start_at as string);
    calendarStore.evenToOpen = subject.value.id;
    return route.replace(`/calendar/day`);
  }

  if (subjectIsTreatmentGoal()) return route.replace(`/user/${subject.value.student?.id}`);
  if (subjectIsOrganization()) return route.replace(`/organizations/${subject.value.id}`);

  return route.replace(`/user/${subject.value.id}`);
};

const downloadRSMFile = async (downloadRsmOrError: boolean) => {
  const path = downloadRsmOrError ? subject.value.file : subject.value.error_file;
  const nameFile = downloadRsmOrError ? subject.value.file : subject.value.error_file;

  if (!path) return;

  const response = await RSMService.dowloadRsmFile(path);
  const content = response.headers['content-type'];

  download(response.data, nameFile, content);
};

const getCustomDescription = (description: string, name: string) => {
  if (description.match('detached') || description.match('attached') || description.match('send a reset'))
    return `${description} ${name}`;
  else return description;
};

const onDownloadDocument = (id: number, name: string) => documentStore.downloadDocument(id, name);

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Icon, {
      name: _unref(custom).icon,
      class: "text-600 text-base text-gray-500"
    }, null, 8, ["name"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_unref(accessTenant) && !_unref(impersonatedBy))
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
              _createElementVNode("span", {
                textContent: _toDisplayString(_unref(accessTenant).full_name_user)
              }, null, 8, _hoisted_5),
              _cache[4] || (_cache[4] = _createTextVNode(" ( as ", -1)),
              (_unref(can)([_unref(PERMISSIONS).DASHBOARD_ACTIVITY_DATE_CLICK]))
                ? (_openBlock(), _createBlock(_component_RouterLink, {
                    key: 0,
                    to: `/user/${_unref(causer).id}`,
                    class: "font-bold",
                    textContent: _toDisplayString(_unref(causer).full_name)
                  }, null, 8, ["to", "textContent"]))
                : (_openBlock(), _createElementBlock("span", {
                    key: 1,
                    textContent: _toDisplayString(_unref(causer).full_name)
                  }, null, 8, _hoisted_6)),
              _cache[5] || (_cache[5] = _createTextVNode(" ) ", -1))
            ]))
          : _createCommentVNode("", true),
        (_unref(accessTenant) && _unref(impersonatedBy))
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
              _createElementVNode("span", {
                textContent: _toDisplayString(_unref(accessTenant).full_name_user)
              }, null, 8, _hoisted_8),
              _cache[6] || (_cache[6] = _createTextVNode(" ( as ", -1)),
              _createVNode(_component_RouterLink, {
                to: `/user/${_unref(causer).id}`,
                class: "font-bold",
                textContent: _toDisplayString(_unref(causer).full_name)
              }, null, 8, ["to", "textContent"]),
              _cache[7] || (_cache[7] = _createTextVNode(" ) ", -1))
            ]))
          : _createCommentVNode("", true),
        (!!!_unref(accessTenant) && _unref(impersonatedBy) && _unref(can)([_unref(PERMISSIONS).DASHBOARD_ACTIVITY_DATE_CLICK]))
          ? (_openBlock(), _createElementBlock("p", _hoisted_9, [
              _createVNode(_component_RouterLink, {
                to: `/user/${_unref(impersonatedBy).id}`,
                class: "font-bold",
                textContent: _toDisplayString(_unref(impersonatedBy).full_name)
              }, null, 8, ["to", "textContent"]),
              _cache[8] || (_cache[8] = _createTextVNode(" ( as ", -1)),
              _createVNode(_component_RouterLink, {
                to: `/user/${_unref(causer).id}`,
                class: "font-bold",
                textContent: _toDisplayString(_unref(causer).full_name)
              }, null, 8, ["to", "textContent"]),
              _cache[9] || (_cache[9] = _createTextVNode(" ) ", -1))
            ]))
          : _createCommentVNode("", true),
        (!!!_unref(accessTenant) && _unref(impersonatedBy) && !_unref(can)([_unref(PERMISSIONS).DASHBOARD_ACTIVITY_DATE_CLICK]))
          ? (_openBlock(), _createElementBlock("p", _hoisted_10, [
              _createElementVNode("span", {
                class: "font-semibold text-teal-500",
                textContent: _toDisplayString(_unref(impersonatedBy).full_name)
              }, null, 8, _hoisted_11),
              _cache[10] || (_cache[10] = _createTextVNode(" ( as ", -1)),
              _createElementVNode("span", {
                class: "font-semibold text-teal-500",
                textContent: _toDisplayString(_unref(causer).full_name)
              }, null, 8, _hoisted_12),
              _cache[11] || (_cache[11] = _createTextVNode(" ) ", -1))
            ]))
          : _createCommentVNode("", true),
        (!_unref(causer).id)
          ? (_openBlock(), _createElementBlock("span", {
              key: 4,
              class: "font-semibold text-teal-500",
              textContent: _toDisplayString(_unref(causer).full_name)
            }, null, 8, _hoisted_13))
          : _createCommentVNode("", true),
        (!_unref(accessTenant) && !_unref(impersonatedBy) && _unref(causer).id && _unref(can)([_unref(PERMISSIONS).DASHBOARD_ACTIVITY_DATE_CLICK]))
          ? (_openBlock(), _createBlock(_component_RouterLink, {
              key: 5,
              to: `/user/${_unref(causer).id}`,
              class: "font-bold",
              textContent: _toDisplayString(_unref(causer).full_name)
            }, null, 8, ["to", "textContent"]))
          : (!_unref(impersonatedBy) && _unref(causer).id && !_unref(can)([_unref(PERMISSIONS).DASHBOARD_ACTIVITY_DATE_CLICK]))
            ? (_openBlock(), _createElementBlock("span", {
                key: 6,
                class: "font-semibold text-teal-500",
                textContent: _toDisplayString(_unref(causer).full_name)
              }, null, 8, _hoisted_14))
            : _createCommentVNode("", true),
        (isImport.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createElementVNode("span", {
                class: "mx-1 dark-light",
                textContent: _toDisplayString(_unref(ACTIVITY_LOG_IMPORT).LABEL)
              }, null, 8, _hoisted_16),
              _createElementVNode("a", {
                href: "#",
                class: "font-bold",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (onDownloadDocument(_unref(properties).new_values.success.id, _unref(properties).new_values.success.name))),
                textContent: _toDisplayString(_unref(properties).new_values.success.name)
              }, null, 8, _hoisted_17)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("span", {
          class: "mx-1 dark-light",
          textContent: _toDisplayString(getCustomDescription(_unref(custom).description, _unref(subject).full_name))
        }, null, 8, _hoisted_18),
        (subjectIsBulkAttendance())
          ? (_openBlock(), _createElementBlock("span", {
              key: 8,
              class: "dark-light",
              textContent: _toDisplayString(bulkImportText())
            }, null, 8, _hoisted_19))
          : (!isImport.value && !subjectIsRsmImport() && !subjectIsImpersonate())
            ? (_openBlock(), _createElementBlock("a", {
                key: 9,
                class: "font-bold",
                onClick: routeToRedirect,
                textContent: _toDisplayString(subjectText())
              }, null, 8, _hoisted_20))
            : (subjectIsRsmImport())
              ? (_openBlock(), _createElementBlock("a", {
                  key: 10,
                  class: "font-semibold text-color-green",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (downloadRSMFile(true))),
                  textContent: _toDisplayString(subjectText())
                }, null, 8, _hoisted_21))
              : _createCommentVNode("", true),
        (subjectIsRsmImport())
          ? (_openBlock(), _createElementBlock("span", {
              key: 11,
              class: "dark-light",
              textContent: _toDisplayString(evaluateStatusToImport())
            }, null, 8, _hoisted_22))
          : _createCommentVNode("", true),
        (subjectIsRsmImport() && _unref(subject).status === _unref(STATUS_IMPORT_RSM).ERRORS)
          ? (_openBlock(), _createElementBlock("a", {
              key: 12,
              class: "font-bold text-color-red",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (downloadRSMFile(false))),
              textContent: 'Download Error Report'
            }))
          : (subjectIsImpersonate())
            ? (_openBlock(), _createElementBlock("a", {
                key: 13,
                class: "font-bold",
                onClick: routeToRedirect,
                textContent: _toDisplayString(subjectText())
              }, null, 8, _hoisted_23))
            : _createCommentVNode("", true),
        (isImport.value && _unref(custom).description === _unref(ACTIVITY_LOG_IMPORT).FAILURE)
          ? (_openBlock(), _createElementBlock("a", {
              key: 14,
              href: "#",
              class: "font-bold text-color-red",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (onDownloadDocument(_unref(properties).new_values.error.id, _unref(properties).new_values.error.name))),
              textContent: '- Download Error Report'
            }))
          : _createCommentVNode("", true),
        (!_unref(accessTenant) && !_unref(impersonatedBy) && !_unref(causer))
          ? (_openBlock(), _createElementBlock("span", {
              key: 15,
              class: "dark-light",
              innerHTML: _unref(changes) ? ` <strong>${_unref(description)}</strong> ${formatChanges(_unref(changes))}` : ''
            }, null, 8, _hoisted_24))
          : _createCommentVNode("", true),
        (
            [
              _unref(ACTIVITY_EVENT_TYPE).PERSON_ARCHIVE,
              _unref(ACTIVITY_EVENT_TYPE).PERSON_CREATE,
              _unref(ACTIVITY_EVENT_TYPE).PERSON_INFORMATION_CHANGE,
              _unref(ACTIVITY_EVENT_TYPE).PERSON_UNARCHIVE,
            ].includes(_unref(event))
          )
          ? (_openBlock(), _createElementBlock("span", {
              key: 16,
              class: "text-teal-500",
              textContent: _toDisplayString(_unref(subject).full_name)
            }, null, 8, _hoisted_25))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("small", {
        textContent: _toDisplayString(_unref(timeAgo))
      }, null, 8, _hoisted_26)
    ])
  ]))
}
}

})